import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  NgZone,
  OnInit,
  Output,
  OnDestroy
} from '@angular/core';
import { StatusEnum } from '@seb/web-design-system/core';
import { TurnstileOptions } from '../../../../models';
import { CaptchaTurnstileService, ToasterService } from '../../../../services';

declare global {
  interface Window {
    onloadTurnstileCallback: () => void;
    turnstile: {
      render: (idOrContainer: string | HTMLElement, options: TurnstileOptions) => string;
      execute: (idOrContainer: string | HTMLElement, jsParams?: TurnstileOptions) => string;
      reset: (widgetIdOrContainer: string | HTMLElement) => void;
      getResponse: (widgetIdOrContainer: string | HTMLElement) => string | undefined;
      remove: (widgetIdOrContainer: string | HTMLElement) => void;
    };
  }
}

const CALLBACK_NAME = 'onloadTurnstileCallback';
type SupportedVersion = '0';

@Component({
  selector: 'wc-captcha-turnstile',
  templateUrl: './captcha-turnstile.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true
})
export class CaptchaTurnstileComponent implements OnInit, OnDestroy {
  @Output() captchaTurnstileSuccess = new EventEmitter<void>();
  @Output() captchaTurnstileError = new EventEmitter<void>();
  @Output() resolved = new EventEmitter<string | null>();

  captchaTurnstileKey: string;
  action?: string;
  cData?: string;
  theme: 'light' | 'dark' | 'auto' = 'auto';
  version: SupportedVersion = '0';
  tabIndex?: number;
  appearance: 'execute';

  private widgetId!: string;

  constructor(
    private captchaTurnstileService: CaptchaTurnstileService,
    private toasterService: ToasterService,
    private elementRef: ElementRef<HTMLElement>,
    private zone: NgZone,
    private cdRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.captchaTurnstileService.captchaTurnstileKey$.subscribe((key) => {
      this.captchaTurnstileKey = key;
      this.cdRef.markForCheck();
    });
  }

  execute(): void {
    const turnstileOptions: TurnstileOptions = {
      sitekey: this.captchaTurnstileKey,
      theme: this.theme,
      tabindex: this.tabIndex,
      action: this.action,
      cData: this.cData,
      appearance: this.appearance,
      callback: (token: string) => this.zone.run(() => this.verifyCaptcha(token)),
      'expired-callback': () => this.zone.run(() => this.reset())
    };

    const script = document.createElement('script');
    window[CALLBACK_NAME] = () =>
      this.elementRef?.nativeElement &&
      (this.widgetId = window.turnstile.execute(this.elementRef.nativeElement, turnstileOptions));

    script.src = `https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=${CALLBACK_NAME}`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }

  verifyCaptcha(token: string): void {
    this.captchaTurnstileService.verifyCaptcha(token).subscribe((isSuccess) => {
      if (isSuccess) {
        this.captchaTurnstileSuccess.emit();
      } else {
        this.handleError();
      }
    });
  }

  onCaptchaError(): void {
    this.handleError();
  }

  private handleError(): void {
    this.captchaTurnstileError.emit();
    this.toasterService.sendToasterEvent({
      status: StatusEnum.error,
      title: 'recaptcha.errorTitle',
      description: 'recaptcha.errorDescription'
    });
  }

  reset(): void {
    if (this.widgetId) {
      this.resolved.emit(null);
      window.turnstile.reset(this.widgetId);
    }
  }

  ngOnDestroy(): void {
    if (this.widgetId) {
      window.turnstile.remove(this.widgetId);
    }
  }
}
